import React from 'react';
import { useCollaboratorByIdQuery } from '@/features/Collaborators/Data';
import { BaseModal } from '@/components/shared/BaseModal';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorInfoHeader } from '@/features/Collaborators/Shared/CollaboratorInfoHeader';

interface CollaboratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    collaboratorId: string | null;
    title: string;
    actions?: React.ReactNode;
    children?: (collaborator: Collaborator) => React.ReactNode;
    maxWidth?: string;
}

const CollaboratorModal: React.FC<CollaboratorModalProps> = ({
    isOpen,
    onClose,
    collaboratorId,
    title,
    actions,
    children,
    maxWidth,
}) => {
    // Buscar dados do colaborador
    const {
        data: collaborator,
        isLoading,
        isError,
        error
    } = useCollaboratorByIdQuery(collaboratorId || undefined);

    if (!isOpen) return null;

    // Loading state
    if (isLoading) {
        const loadingTitle = (
            <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Carregando dados do colaborador...
                </p>
            </div>
        );

        return (
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                title={loadingTitle}
                maxWidth={maxWidth}
            >
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-2 text-gray-500">Carregando dados do colaborador...</span>
                </div>
            </BaseModal>
        );
    }

    // Error state
    if (isError) {
        const errorTitle = (
            <div>
                <h3 className="text-lg font-medium leading-6 text-red-900">
                    Erro ao carregar colaborador
                </h3>
            </div>
        );

        const errorActions = (
            <>
                <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary mr-3"
                >
                    Fechar
                </button>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="btn-primary"
                >
                    Tentar novamente
                </button>
            </>
        );

        return (
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                title={errorTitle}
                actions={errorActions}
                maxWidth={maxWidth}
            >
                <div className="text-center py-12">
                    <div className="text-red-600 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar colaborador</h3>
                    <p className="text-gray-500 mb-4">
                        {error instanceof Error ? error.message : 'Não foi possível carregar os dados do colaborador.'}
                    </p>
                </div>
            </BaseModal>
        );
    }

    // Not found state
    if (!collaborator) {
        const notFoundTitle = (
            <div>
                <h3 className="text-lg font-medium leading-6 text-yellow-900">
                    Colaborador não encontrado
                </h3>
            </div>
        );

        const notFoundActions = (
            <button
                type="button"
                onClick={onClose}
                className="btn-primary"
            >
                Fechar
            </button>
        );

        return (
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                title={notFoundTitle}
                actions={notFoundActions}
                maxWidth={maxWidth}
            >
                <div className="text-center py-12">
                    <div className="text-yellow-600 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Colaborador não encontrado</h3>
                    <p className="text-gray-500 mb-4">
                        O colaborador com ID "{collaboratorId}" não foi encontrado.
                    </p>
                </div>
            </BaseModal>
        );
    }

    // Success state - colaborador carregado
    const modalTitle = (
        <CollaboratorInfoHeader
            name={collaborator.name}
            email={collaborator.email || collaborator.personalEmail || ''}
            photoUrl={collaborator.photoUrl}
            backgroundColor="bg-transparent"
        />
    );

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            actions={actions}
            maxWidth={maxWidth}
        >
            {/* Conteúdo do modal */}
            {children ? children(collaborator) : (
                <div className="p-6">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                        <p className="text-gray-600 text-sm text-center">
                            CollaboratorModal configurado. Aguardando componentes filhos.
                        </p>
                        <div className="mt-2 text-xs text-gray-500 text-center">
                            Colaborador: <span className="font-mono bg-gray-200 px-1 rounded">{collaborator.name}</span>
                        </div>
                    </div>
                </div>
            )}
        </BaseModal>
    );
};

export default CollaboratorModal; 